import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';

import { AlertConfirmState, AlertToast, AlertType } from './alert.model';
import { AlertService } from './alert.service';

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './alert.component.html'
})
export class AlertComponent {
  private readonly alertService = inject(AlertService);
  readonly toasts$ = this.alertService.toasts$;
  readonly confirm$ = this.alertService.confirm$;

  dismissToast(toastId: number): void {
    this.alertService.dismissToast(toastId);
  }

  confirm(): void {
    this.alertService.confirmCurrent();
  }

  cancel(): void {
    this.alertService.cancelCurrent();
  }

  trackToast(_: number, toast: AlertToast): number {
    return toast.id;
  }

  toneClasses(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'border-emerald-200 bg-[linear-gradient(135deg,rgba(79,209,165,0.18)_0%,rgba(255,255,255,0.98)_42%,rgba(255,255,255,1)_100%)]';
      case 'error':
        return 'border-rose-200 bg-[linear-gradient(135deg,rgba(227,93,93,0.18)_0%,rgba(255,255,255,0.98)_42%,rgba(255,255,255,1)_100%)]';
      case 'warning':
        return 'border-amber-200 bg-[linear-gradient(135deg,rgba(255,179,71,0.22)_0%,rgba(255,255,255,0.98)_42%,rgba(255,255,255,1)_100%)]';
      default:
        return 'border-blue-200 bg-[linear-gradient(135deg,rgba(51,102,255,0.18)_0%,rgba(255,255,255,0.98)_42%,rgba(255,255,255,1)_100%)]';
    }
  }

  badgeClasses(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'bg-emerald-100 text-emerald-700 ring-emerald-200';
      case 'error':
        return 'bg-rose-100 text-rose-700 ring-rose-200';
      case 'warning':
        return 'bg-amber-100 text-amber-700 ring-amber-200';
      default:
        return 'bg-blue-100 text-blue-700 ring-blue-200';
    }
  }

  accentClasses(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'from-emerald-400 to-emerald-500';
      case 'error':
        return 'from-rose-400 to-rose-500';
      case 'warning':
        return 'from-amber-400 to-amber-500';
      default:
        return 'from-brand-accent to-brand-primary-medium';
    }
  }

  iconLabel(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'Listo';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Atencion';
      default:
        return 'Info';
    }
  }

  iconPath(type: AlertType): string {
    switch (type) {
      case 'success':
        return 'M5 13l4 4L19 7';
      case 'error':
        return 'M6 6l12 12M18 6L6 18';
      case 'warning':
        return 'M12 9v4m0 4h.01M10.29 3.86l-7.5 13A1 1 0 0 0 3.65 18h16.7a1 1 0 0 0 .86-1.5l-7.5-13a1 1 0 0 0-1.72 0Z';
      default:
        return 'M12 8h.01M11 12h1v4h1';
    }
  }

  isWarning(confirmState: AlertConfirmState | null): boolean {
    return confirmState?.type === 'warning';
  }
}
